<template>
  <div>
    <!-- Enhanced Filters Section -->
    <b-card class="mb-3">
      <b-card-header>
        <h5 class="mb-0">
          <feather-icon
            icon="FilterIcon"
            class="mr-1"
          />
          Filtres
        </h5>
      </b-card-header>
      <b-card-body>
        <b-row class="mt-1">
          <b-col
            v-if="user.role === 'admin'"
            md="3"
          >
            <b-form-group
              label="Fournisseur"
              label-for="supplier"
            >
              <v-select
                v-model="filters.supplier"
                :clearable="true"
                placeholder="Sélectionner un fournisseur"
                label="username"
                :options="suppliers"
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col
            v-if="user.role === 'admin'"
            md="3"
          >
            <b-form-group
              label="Dépôt"
              label-for="repository"
            >
              <v-select
                v-model="filters.repository"
                :clearable="true"
                placeholder="Sélectionner un dépôt"
                label="name"
                :options="repositories"
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col md="3">
            <b-form-group
              label="Statut de validation"
              label-for="validation"
            >
              <b-form-select
                v-model="filters.isValidated"
                :options="validationSelectOptions"
                @change="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col md="3">
            <b-form-group
              label="Date début"
              label-for="dateFrom"
            >
              <b-form-datepicker
                v-model="filters.dateFrom"
                v-bind="datePickerConfig"
                placeholder="Date de début"
                reset-button
                close-button
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col md="3">
            <b-form-group
              label="Date fin"
              label-for="dateTo"
            >
              <b-form-datepicker
                v-model="filters.dateTo"
                v-bind="datePickerConfig"
                placeholder="Date de fin"
                reset-button
                close-button
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col md="3">
            <b-form-group
              label="Prix minimum (TND)"
              label-for="priceMin"
            >
              <b-form-input
                v-model="filters.priceMin"
                type="number"
                step="0.001"
                placeholder="0.000"
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col md="3">
            <b-form-group
              label="Prix maximum (TND)"
              label-for="priceMax"
            >
              <b-form-input
                v-model="filters.priceMax"
                type="number"
                step="0.001"
                placeholder="0.000"
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Reset Filters Button Row -->
        <b-row class="mt-2">
          <b-col class="d-flex justify-content-end">
            <b-button
              variant="outline-secondary"
              @click="clearFilters"
            >
              <feather-icon
                icon="XIcon"
                class="mr-1"
              />
              Effacer les filtres
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Action Buttons -->
    <b-row class="mb-3">
      <b-col class="d-flex justify-content-end">
        <b-button
          v-if="user.role === 'admin' || user.role === 'manager'"
          :to="{ name: 'bon-retour-add' }"
          variant="gradient-primary"
          class="ml-1"
        >
          <feather-icon icon="PlusIcon" />
          <span class="ml-1">Ajouter bon retour</span>
        </b-button>
      </b-col>
    </b-row>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="text-center mb-2"
    >
      <b-spinner
        variant="primary"
        label="Chargement..."
      />
      <p class="mt-2">
        Chargement des bons de retour...
      </p>
    </div>

    <!-- Empty State - No Data at All -->
    <b-card
      v-else-if="!isLoading && returnVouchers.length === 0 && !hasActiveFilters"
      class="text-center"
    >
      <b-card-body class="py-5">
        <feather-icon
          icon="PackageIcon"
          size="48"
          class="text-muted mb-3"
        />
        <h4 class="text-muted">
          Aucun bon de retour
        </h4>
        <p class="text-muted mb-3">
          Il n'y a pas encore de bons de retour dans le système.
        </p>
        <b-button
          v-if="user.role === 'admin' || user.role === 'manager'"
          :to="{ name: 'bon-retour-add' }"
          variant="primary"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-1"
          />
          Créer le premier bon de retour
        </b-button>
      </b-card-body>
    </b-card>

    <!-- Empty State - No Results for Filters -->
    <b-card
      v-else-if="!isLoading && returnVouchers.length === 0 && hasActiveFilters"
      class="text-center"
    >
      <b-card-body class="py-5">
        <feather-icon
          icon="SearchIcon"
          size="48"
          class="text-muted mb-3"
        />
        <h4 class="text-muted">
          Aucun résultat trouvé
        </h4>
        <p class="text-muted mb-3">
          Aucun bon de retour ne correspond aux critères de recherche sélectionnés.
          Essayez de modifier vos filtres pour élargir votre recherche.
        </p>
        <b-button
          variant="outline-primary"
          @click="clearFilters"
        >
          <feather-icon
            icon="RefreshCwIcon"
            class="mr-1"
          />
          Réinitialiser les filtres
        </b-button>
      </b-card-body>
    </b-card>

    <!-- Data Table -->
    <vue-good-table
      v-else
      :columns="tableColumns"
      :rows="returnVouchers"
      :search-options="{ enabled: true, externalQuery: searchTerm }"
      :pagination-options="{ enabled: true, perPage: pageLength }"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <div v-if="props.column.field === 'id'">
          <qrcode-vue
            :value="props.row.id.toString()"
            :size="size"
            level="H"
          />
          <p>{{ props.row.id }}</p>
        </div>

        <span v-else-if="props.column.field === 'is_validated'">
          <div>
            <b-badge
              :variant="props.row.is_validated ? 'success' : 'warning'"
              class="mb-1"
            >
              <feather-icon
                :icon="props.row.is_validated ? 'CheckCircleIcon' : 'ClockIcon'"
                size="12"
                class="mr-1"
              />
              {{ props.row.is_validated ? 'Validé' : 'En attente' }}
            </b-badge>
            <div
              v-if="props.row.is_validated && props.row.validated_at"
              class="mt-1"
            >
              <small class="text-muted d-block">
                <feather-icon
                  icon="CalendarIcon"
                  size="12"
                  class="mr-1"
                />
                {{ formatFn(props.row.validated_at) }}
              </small>
            </div>
          </div>
        </span>

        <span v-else-if="props.column.field === 'total_price'">
          <strong>{{ formatPrice(props.row.total_price) }} TND</strong>
        </span>

        <span v-else-if="props.column.field === 'total_return_price'">
          <strong class="text-danger">{{ formatPrice(props.row.total_return_price) }} TND</strong>
        </span>

        <span v-else-if="props.column.field === 'action'">
          <b-button
            size="sm"
            variant="outline-info"
            :to="{ name: 'bon-retour-show', params: { id: props.row.id } }"
            class="mr-1"
          >
            <feather-icon
              icon="EyeIcon"
              class="mr-1"
            />
            Voir
          </b-button>

          <!-- Validate button for supplier only, if not validated -->
          <b-button
            v-if="user.role === 'supplier' && !props.row.is_validated"
            size="sm"
            variant="success"
            :disabled="props.row._isValidating"
            @click="confirmValidateVoucher(props.row)"
          >
            <feather-icon
              icon="CheckCircleIcon"
              class="mr-1"
            />
            Valider automatiquement
          </b-button>
        </span>

        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap">Affichage 1 à</span>
            <b-form-select
              v-model="pageLength"
              :options="['50','100','500']"
              class="mx-1"
              @input="(value) => props.perPageChanged({ currentPerPage: value })"
            />
            <span class="text-nowrap">de {{ count }} entrées</span>
          </div>

          <div>
            <b-pagination
              v-model="currentPage"
              :total-rows="count"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @input="pageNext"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>
  </div>
</template>

<script>
import {
  BPagination,
  BFormGroup,
  BFormSelect,
  BButton,
  BFormInput,
  BSpinner,
  BCol,
  BRow,
  BCard,
  BCardHeader,
  BCardBody,
  BBadge,
  BFormDatepicker,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import QrcodeVue from 'qrcode.vue'
import vSelect from 'vue-select'
import storeAuth from '@/store/store'

export default {
  components: {
    VueGoodTable,
    BPagination,
    BFormGroup,
    BFormSelect,
    BButton,
    BSpinner,
    QrcodeVue,
    vSelect,
    BCol,
    BRow,
    BCard,
    BCardHeader,
    BCardBody,
    BBadge,
    BFormInput,
    BFormDatepicker,
  },
  data() {
    return {
      size: 40,
      isLoading: false,
      pageLength: 100,
      columns: [
        {
          label: 'Reference',
          field: 'id',
        },
        {
          label: 'Fournisseur',
          field: 'supplier',
        },
        {
          label: 'Nombre de colis',
          field: 'orders_count',
        },
        {
          label: 'Prix total (TND)',
          field: 'total_price',
        },
        {
          label: 'Prix retour (TND)',
          field: 'total_return_price',
        },
        {
          label: 'Statut',
          field: 'is_validated',
        },
        {
          label: 'Date de création',
          field: 'created_at',
          formatFn: this.formatFn,
        },
        {
          label: 'Action',
          field: 'action',
        },
      ],
      returnVouchers: [],
      searchTerm: '',
      currentPage: 1,
      count: 0,
      suppliers: [],
      repositories: [],
      filters: {
        supplier: null,
        repository: null,
        isValidated: '',
        dateFrom: '',
        dateTo: '',
        priceMin: '',
        priceMax: '',
      },
      validationSelectOptions: [
        { value: '', text: 'Tous les statuts' },
        { value: true, text: 'Validé' },
        { value: false, text: 'En attente' },
      ],
      user: {},
      datePickerConfig: {
        locale: 'fr-FR',
        startWeekday: 1,
        labelHelp: 'Utilisez les flèches pour naviguer',
        labelPrevYear: 'Année précédente',
        labelPrevMonth: 'Mois précédent',
        labelCurrentMonth: 'Mois actuel',
        labelNextMonth: 'Mois suivant',
        labelNextYear: 'Année suivante',
        labelToday: 'Aujourd\'hui',
        labelSelected: 'Date sélectionnée',
        labelNoDateSelected: 'Aucune date sélectionnée',
      },
    }
  },
  computed: {
    tableColumns() {
      if (this.user.role === 'admin') {
        return this.columns
      }
      return this.columns.filter(column => !['supplier', 'repository'].includes(column.field))
    },
    hasActiveFilters() {
      return Object.values(this.filters).some(filter => filter !== null && filter !== '' && filter !== undefined)
    },
  },
  created() {
    this.user = storeAuth.state.user
    this.getReturnVouchers(1)
    if (this.user.role === 'admin') {
      this.getSuppliers()
      this.getRepositories()
    }
  },
  methods: {
    async confirmValidateVoucher(voucher) {
      try {
        const netAmount = (voucher.total_price - voucher.total_return_price).toFixed(3)

        const confirmed = await this.$bvModal.msgBoxConfirm(
          `Vous êtes sur le point de valider automatiquement le bon de retour #${voucher.id}.

⚠️ ATTENTION : Cette action est irréversible et ne peut pas être annulée une fois confirmée.

📋 RÉSUMÉ DU BON DE RETOUR :
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

📦 Nombre de colis retournés : ${voucher.orders_count} colis
💰 Prix total TTC : ${this.formatPrice(voucher.total_price)} TND
📉 Frais de retour : ${this.formatPrice(voucher.total_return_price)} TND
💵 Montant net à recevoir : ${netAmount} TND

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

✅ En validant automatiquement ce bon, vous confirmez :
   • Avoir reçu tous les colis retournés par la société de livraison
   • Accepter les frais de retour appliqués pour les échecs de livraison
   • Que le montant net calculé est correct et acceptable
   • Que les colis de ce bon passeront au statut "Retour payé recu"

📌 NOTE : Ce bon concerne les colis qui n'ont pas pu être livrés après plusieurs tentatives et qui vous sont définitivement retournés.

Êtes-vous certain de vouloir procéder à la validation ?`,
          {
            title: '🔐 Validation automatique du bon de retour',
            size: 'lg',
            buttonSize: 'sm',
            okVariant: 'success',
            okTitle: '✅ Oui, valider automatiquement',
            cancelVariant: 'outline-secondary',
            cancelTitle: '❌ Annuler',
            footerClass: 'p-3 border-top',
            headerClass: 'p-3 border-bottom bg-light',
            bodyClass: 'p-4',
            hideHeaderClose: false,
            centered: true,
            noCloseOnBackdrop: true,
            noCloseOnEsc: true,
            modalClass: 'validation-confirmation-modal',
          },
        )

        if (confirmed) {
          await this.validateVoucher(voucher)
        }
      } catch (error) {
        // User cancelled or closed dialog - do nothing
      }
    },
    async validateVoucher(voucher) {
      // Optimistic UI: set loading state on the row
      this.$set(voucher, '_isValidating', true)
      try {
        const response = await axios.patch('api/return-vouchers/validate-return-voucher-orders/', {
          voucher_id: voucher.id,
        })
        if (response.data && response.data.success) {
          // Avoid direct assignment to function parameter (no-param-reassign)
          const idx = this.returnVouchers.findIndex(v => v.id === voucher.id)
          if (idx !== -1) {
            // Use Vue.set to update properties reactively
            this.$set(this.returnVouchers, idx, {
              ...this.returnVouchers[idx],
              is_validated: true,
              validated_at: response.data.validated_at,
              _isValidating: false,
            })
          }
          this.showToast('success', 'top-right', response.data.message || 'Bon de retour validé avec succès.')
        } else {
          this.showToast('danger', 'top-right', response.data.message || 'Erreur lors de la validation.')
        }
      } catch (error) {
        let msg = 'Erreur lors de la validation.'
        if (error.response && error.response.data && error.response.data.message) {
          msg = error.response.data.message
        }
        this.showToast('danger', 'top-right', msg)
      } finally {
        // Always clear loading state
        const idx = this.returnVouchers.findIndex(v => v.id === voucher.id)
        if (idx !== -1) {
          this.$set(this.returnVouchers[idx], '_isValidating', false)
        }
      }
    },
    async getSuppliers() {
      try {
        const response = await axios.get('api/auth/users/suppliers/')
        this.suppliers = response.data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching suppliers:', error)
      }
    },
    async getRepositories() {
      try {
        const response = await axios.get('api/repositories/')
        this.repositories = response.data.results || response.data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching repositories:', error)
      }
    },
    pageNext(currentPage) {
      this.getReturnVouchers(currentPage)
    },
    formatFn(date) {
      if (!date) return null
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(2, '0')} | ${h}`
    },
    formatPrice(price) {
      return price ? price.toFixed(3) : '0.000'
    },
    applyFilters() {
      this.currentPage = 1
      this.getReturnVouchers(1)
    },
    clearFilters() {
      this.filters = {
        supplier: null,
        repository: null,
        isValidated: '',
        dateFrom: '',
        dateTo: '',
        priceMin: '',
        priceMax: '',
      }
      this.currentPage = 1
      this.getReturnVouchers(1)
    },
    buildFilterParams() {
      const params = { p: this.currentPage }

      if (this.filters.supplier) {
        params.supplier = this.filters.supplier.id
      }
      if (this.filters.repository) {
        params.repository = this.filters.repository.id
      }
      if (this.filters.isValidated !== '') {
        params.is_validated = this.filters.isValidated
      }
      if (this.filters.dateFrom) {
        params.created_at__gte = this.filters.dateFrom
      }
      if (this.filters.dateTo) {
        params.created_at__lte = this.filters.dateTo
      }
      if (this.filters.priceMin) {
        params.total_price__gte = this.filters.priceMin
      }
      if (this.filters.priceMax) {
        params.total_price__lte = this.filters.priceMax
      }

      return params
    },
    async getReturnVouchers(currentPage) {
      this.isLoading = true
      try {
        const params = this.buildFilterParams()
        params.p = currentPage

        const { data } = await axios.get('api/return-vouchers/', { params })
        this.returnVouchers = data.results
        this.count = data.count
      } catch (error) {
        this.showToast('danger', 'top-right', 'Erreur lors du chargement des données')
      } finally {
        this.isLoading = false
      }
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
    clearDateFilter(field) {
      this.filters[field] = null
      this.applyFilters()
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
